'use client'

import type { ToolUIPart } from 'ai'
import { ChevronDownIcon } from 'lucide-react'
import type { ComponentProps, ReactNode } from 'react'
import { Children, isValidElement } from 'react'
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible'
import { GlowingDot } from '@/components/ui/glowing-dot'
import { useSupabaseAuth } from '@/lib/supabase'
import { cn } from '@/lib/utils'
import { CodeBlock } from './code-block'

// Allowlist of user emails who can see collapsible tools
const TOOL_COLLAPSIBLE_ALLOWLIST = [
  // Add allowed email addresses here
  // Example: 'user@example.com',
  'w@pipedreamer.ai',
  'cmay@pipedreamer.ai',
  'cmay3249@gmail.com',
  'spherecollider@gmail.com',
]

const isEmailAllowlisted = (email: string | undefined | null): boolean => {
  // Always show collapsible tools in dev environment
  if (import.meta.env.DEV) return true
  if (!email) return false
  return TOOL_COLLAPSIBLE_ALLOWLIST.includes(email.toLowerCase())
}

export type ToolHeaderProps = {
  title?: string
  type: ToolUIPart['type']
  state: ToolUIPart['state']
  className?: string
}

const getStatusBadge = (status: ToolUIPart['state']) => {
  const icons: Record<ToolUIPart['state'], ReactNode> = {
    'input-streaming': <GlowingDot size="sm" color="default" />,
    'input-available': <GlowingDot size="sm" color="blue" />,
    'approval-requested': <GlowingDot size="sm" color="yellow" />,
    'approval-responded': <GlowingDot size="sm" color="blue" />,
    'output-available': <GlowingDot size="sm" color="green" />,
    'output-error': <GlowingDot size="sm" color="red" />,
    'output-denied': <GlowingDot size="sm" color="orange" />,
  }

  return icons[status]
}

export type ToolProps = ComponentProps<typeof Collapsible>

export const Tool = ({ className, children, ...props }: ToolProps) => {
  const { user } = useSupabaseAuth()
  const isAllowlisted = isEmailAllowlisted(user?.email)

  // If not allowlisted, render just the header
  if (!isAllowlisted) {
    // Find ToolHeader in children and extract its props
    // Check for props that uniquely identify ToolHeader (type and state)
    let headerProps: ToolHeaderProps | null = null
    Children.forEach(children, (child) => {
      if (
        isValidElement(child) &&
        'type' in child.props &&
        'state' in child.props &&
        typeof child.props.type === 'string' &&
        child.props.type.startsWith('tool-')
      ) {
        headerProps = child.props as ToolHeaderProps
      }
    })

    if (headerProps) {
      return (
        <div className={cn('not-prose mb-2 w-full', className)}>
          <div className="flex w-full items-center justify-between gap-2 py-1">
            <div className="flex items-center gap-2 pl-1">
              {getStatusBadge(headerProps.state)}
              <span className="font-medium text-xs text-left">
                {headerProps.title ??
                  headerProps.type.split('-').slice(1).join('-')}
              </span>
            </div>
          </div>
        </div>
      )
    }
  }

  // If allowlisted, render full collapsible
  return (
    <Collapsible className={cn('not-prose mb-2 w-full', className)} {...props}>
      {children}
    </Collapsible>
  )
}

export const ToolHeader = ({
  className,
  title,
  type,
  state,
  ...props
}: ToolHeaderProps) => (
  <CollapsibleTrigger
    className={cn(
      'flex w-full items-center justify-between gap-2 py-1',
      className
    )}
    {...props}
  >
    <div className="flex items-center gap-2 pl-1">
      {getStatusBadge(state)}
      <span className="font-medium text-xs text-left">
        {title ?? type.split('-').slice(1).join('-')}
      </span>
    </div>
    <ChevronDownIcon className="size-4 text-muted-foreground transition-transform group-data-[state=open]:rotate-180" />
  </CollapsibleTrigger>
)

export type ToolContentProps = ComponentProps<typeof CollapsibleContent>

export const ToolContent = ({ className, ...props }: ToolContentProps) => (
  <CollapsibleContent
    className={cn(
      'data-[state=closed]:fade-out-0 data-[state=closed]:slide-out-to-top-2 data-[state=open]:slide-in-from-top-2 text-popover-foreground outline-none data-[state=closed]:animate-out data-[state=open]:animate-in',
      className
    )}
    {...props}
  />
)

export type ToolInputProps = ComponentProps<'div'> & {
  input: ToolUIPart['input']
}

export const ToolInput = ({ className, input, ...props }: ToolInputProps) => (
  <div className={cn('space-y-1 overflow-hidden py-1', className)} {...props}>
    <span className="font-medium text-muted-foreground text-xs uppercase tracking-wide">
      Input
    </span>
    <div className="rounded-md bg-muted/50">
      <CodeBlock code={JSON.stringify(input, null, 2)} language="json" />
    </div>
  </div>
)

export type ToolOutputProps = ComponentProps<'div'> & {
  output: ToolUIPart['output']
  errorText: ToolUIPart['errorText']
}

export const ToolOutput = ({
  className,
  output,
  errorText,
  ...props
}: ToolOutputProps) => {
  if (!(output || errorText)) {
    return null
  }

  let Output = <div>{output as ReactNode}</div>

  if (typeof output === 'object' && !isValidElement(output)) {
    Output = (
      <CodeBlock code={JSON.stringify(output, null, 2)} language="json" />
    )
  } else if (typeof output === 'string') {
    Output = <CodeBlock code={output} language="json" />
  }

  return (
    <div className={cn('space-y-1 py-1', className)} {...props}>
      <span className="font-medium text-muted-foreground text-xs uppercase tracking-wide">
        {errorText ? 'Error' : 'Output'}
      </span>
      <div
        className={cn(
          'overflow-x-auto rounded-md text-xs [&_table]:w-full',
          errorText
            ? 'bg-destructive/10 text-destructive'
            : 'bg-muted/50 text-foreground'
        )}
      >
        {errorText && <div>{errorText}</div>}
        {Output}
      </div>
    </div>
  )
}
